import type { CSSProperties } from "react";

import { builderImageUrl } from "~/hec/utils/builder-image-url";

/**
 * Returns a background style object for hero components,
 * supporting mobile-specific images and gradients.
 *
 * @deprecated Prefer `getResponsiveHeroBgStyles` for SSR-safe responsive backgrounds.
 * This function requires a JS-based `isMobile` flag which causes hydration mismatches
 * in streaming SSR environments (e.g., Cloudflare Workers).
 */
export function getHeroBgStyle({
  backgroundType,
  backgroundImage,
  backgroundImageMobile,
  backgroundColor,
  backgroundGradient,
  isMobile,
}: {
  backgroundType: string | undefined;
  backgroundImage?: string;
  backgroundImageMobile?: string;
  backgroundColor?: string;
  backgroundGradient?: string;
  isMobile: boolean;
}): CSSProperties {
  if (backgroundType === "image") {
    if (isMobile && backgroundImageMobile) {
      return {
        backgroundImage: `url(${builderImageUrl(backgroundImageMobile, { width: 800, format: "webp" })})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    } else if (backgroundImage) {
      return {
        backgroundImage: `url(${builderImageUrl(backgroundImage, { width: 1600, format: "webp" })})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    }
  } else if (backgroundType === "color" && backgroundColor) {
    return { backgroundColor };
  } else if (backgroundType === "gradient" && backgroundGradient) {
    return {
      background: backgroundGradient,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  return {};
}

/**
 * SSR-safe responsive background styles for hero components.
 *
 * Returns a base CSSProperties object (for the desktop background) and a CSS
 * string that can be injected into a `<style>` tag to apply the mobile
 * background image via a media query. This avoids any JS-based viewport
 * detection, preventing hydration mismatches in streaming SSR.
 *
 * Usage:
 * ```tsx
 * const { style, mobileStyleTag } = getResponsiveHeroBgStyles({ ... id: heroId });
 * return (
 *   <>
 *     {mobileStyleTag}
 *     <section id={heroId} style={style}>...</section>
 *   </>
 * );
 * ```
 */
export function getResponsiveHeroBgStyles({
  id,
  backgroundType,
  backgroundImage,
  backgroundImageMobile,
  backgroundColor,
  backgroundGradient,
}: {
  /** A unique DOM id for the element so the media-query style can target it. */
  id: string;
  backgroundType: string | undefined;
  backgroundImage?: string;
  backgroundImageMobile?: string;
  backgroundColor?: string;
  backgroundGradient?: string;
}): { style: CSSProperties; mobileCss: string } {
  if (backgroundType === "image") {
    const desktopSrc = builderImageUrl(backgroundImage, {
      width: 1600,
      format: "webp",
    });
    const mobileSrc = builderImageUrl(backgroundImageMobile, {
      width: 800,
      format: "webp",
    });

    const desktopStyle: CSSProperties = desktopSrc
      ? {
          backgroundImage: `url(${desktopSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {};

    // If there's a separate mobile image, emit a media-query CSS block
    const mobileCss =
      mobileSrc && mobileSrc !== desktopSrc
        ? `@media (max-width: 767px) { #${id} { background-image: url(${mobileSrc}) !important; background-size: cover; background-position: center; } }`
        : "";

    return { style: desktopStyle, mobileCss };
  }

  if (backgroundType === "color" && backgroundColor) {
    return { style: { backgroundColor }, mobileCss: "" };
  }

  if (backgroundType === "gradient" && backgroundGradient) {
    return {
      style: {
        background: backgroundGradient,
        backgroundSize: "cover",
        backgroundPosition: "center",
      },
      mobileCss: "",
    };
  }

  return { style: {}, mobileCss: "" };
}
